import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import {
  parseContentDisposition,
  sanitizeFilename,
} from "@utils:parseContentDisposition";
import { storage } from "@utils:token-storage";
import axios, { AxiosError } from "axios";

export interface DocumentTranslation {
  POST: {
    RequestBody: {
      file: File;
      targetLanguage: string;
    };
    Response: {
      blob: Blob;
      filename: string | null;
    };
  };
}

export const useMutationTranslateDocument = (
  botId: string,
  options?: UseMutationOptions<
    DocumentTranslation["POST"]["Response"],
    AxiosError,
    DocumentTranslation["POST"]["RequestBody"]
  >,
) => {
  return useMutation({
    mutationFn: async (
      data: DocumentTranslation["POST"]["RequestBody"],
    ): Promise<{ blob: Blob; filename: string | null }> => {
      const formData = new FormData();
      formData.append("file", data.file);
      formData.append("target_language", data.targetLanguage);

      try {
        // Use axios directly to access response headers (not through wrapper)
        const token = storage.getToken();
        const response = await axios.post(
          `${window.location.origin}/api/translation/document`,
          formData,
          {
            params: { bot_id: botId },
            responseType: "blob",
            headers: {
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          },
        );

        // Extract filename from Content-Disposition header
        const contentDisposition =
          response.headers["content-disposition"] || "";
        const rawFilename = parseContentDisposition(contentDisposition);
        const filename = rawFilename ? sanitizeFilename(rawFilename) : null;

        return {
          blob: response.data,
          filename,
        };
      } catch (error) {
        // Handle 401 Unauthorized - clear token and redirect
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          storage.clearToken();
          window.location.replace(window.location.origin);
        }
        if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
          try {
            error.response.data = JSON.parse(await error.response.data.text());
          } catch {
            error.response.data = undefined;
          }
        }
        return Promise.reject(error);
      }
    },
    ...options,
  });
};
